import IframeResizer from "iframe-resizer-react";
import { useEvent, usePrevious } from "react-use";
import { useState } from "react";
import { removeTrailingSlash } from "Helpers";
import { alpha, Box, CircularProgress, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useCallback } from "react";
import { useEffect } from "react";
import {
  appendHashParams,
  debugHighlight,
  debugHighlightFromText,
  debugNone,
  debugParam,
  debugRepeated,
  debugTags,
  debugUnique,
} from "@incendium/inc-ts-helpers";
import { useSmartIframeContext } from "features/smartIFrame/providers/SmartIFrameProvider";
import { smartIframeDebug } from "features/smartIFrame/utils/smartIframeDebug";

const initialState = {
  isVisible: false,
  coords: { x: 0, y: 0, width: 0, height: 0 },
};

const useSmartIframeStyles = makeStyles(() => ({
  loading: {
    position: "absolute",
    width: "100%",
    height: "100%",
    backdropFilter: `blur(3px)`,
    background: "rgba(255,255, 255, 0.1)",
  },
}));

export const SmartIframe = ({
  src,
  onClick,
  active,
  targets,
  selector,
  repeated,
  targetText,
  useTextMode,
  width = "100%",
}: {
  src: string;
  onClick?: (e: { selector: string; dimensions: any; text?: string }) => void;
  active?: boolean;
  targets?: string[];
  selector: string | undefined;
  repeated: boolean;
  targetText: string | undefined;
  width?: string;
  useTextMode?: boolean;
}) => {
  const { iframeRef, sendMessage, setProductSchemaFound, notifyIframeReady } =
    useSmartIframeContext();

  const [state, setState] = useState(initialState);
  const [loaded, setLoaded] = useState(false);
  const classes = useSmartIframeStyles();
  const prevRepeated = usePrevious(repeated);
  const prevUseTextMode = usePrevious(useTextMode);
  const theme = useTheme();

  const getStyles = (): React.CSSProperties => {
    if (state.isVisible) {
      const { x, y, width, height } = state.coords;
      return {
        position: "fixed",
        top: y - 2,
        left: x - 8,
        width: width + 6,
        height: height + 4,
        border: `2px dashed ${theme.palette.primary.vibrant}`,
        background: alpha(theme.palette.primary.vibrant!, 0.05),
        pointerEvents: "none",
        cursor: "default",
      };
    }
    return { display: "none" };
  };

  const resetRect = useCallback(() => {
    smartIframeDebug("hover", "reset overlay (iframe mouseleave)");
    setState(initialState);
  }, []);

  useEffect(() => {
    setLoaded(false);
    setState(initialState);
    smartIframeDebug("lifecycle", "src changed — resetting loaded/overlay", {
      src,
    });
  }, [src]);

  useEffect(() => {
    setState(initialState);
    smartIframeDebug("sync", "prop sync effect", {
      active,
      targets,
      selector,
      repeated,
      targetText,
      useTextMode,
      prevRepeated,
    });
    if (prevRepeated !== repeated) {
      sendMessage({ type: repeated ? debugRepeated : debugUnique });
    }
    if (targets) {
      sendMessage({
        type: debugTags,
        data: targets,
      });
    }
    if (selector !== "") {
      sendMessage({ type: debugHighlight, data: selector });
    }

    if (targetText !== "" && selector === "") {
      sendMessage({ type: debugHighlightFromText, data: targetText });
    }

    if (!active) {
      setState(initialState);
      sendMessage({ type: debugNone });
    }
  }, [
    targets?.length,
    targets,
    active,
    selector,
    repeated,
    prevRepeated,
    targetText,
    prevUseTextMode,
    useTextMode,
    sendMessage,
  ]);

  const logElement = useCallback(
    (e: MessageEvent) => {
      if (e.data.type) {
        switch (e.data.type) {
          case "start":
          case "sendTargets": {
            smartIframeDebug("message←", e.data.type, {
              propTargets: targets,
              willResendPropTargets: Boolean(targets),
            });
            notifyIframeReady();
            if (targets) {
              sendMessage({ type: debugTags, data: targets });
            }
            break;
          }
          case "click": {
            smartIframeDebug("message←", "click", {
              selector: e.data.selector,
              text: e.data.text,
            });
            onClick && onClick(e.data);
            break;
          }
          case "mouseover": {
            const rect: DOMRect = e.data.dimensions;
            const iframeRect = document
              .getElementById("smartiframe")
              ?.getBoundingClientRect();

            if (!iframeRect) {
              smartIframeDebug("hover", "mouseover dropped — #smartiframe missing", {
                selector: e.data.selector,
                dimensions: rect,
              });
              return;
            }

            const next = {
              isVisible: true,
              coords: {
                x: rect.x + iframeRect.x,
                y: rect.y + iframeRect.y - 5,
                width: rect.width + 10,
                height: rect.height + 10,
              },
            };
            smartIframeDebug("hover", "mouseover → show overlay", {
              selector: e.data.selector,
              text: e.data.text,
              elementRect: rect,
              iframeRect: {
                x: iframeRect.x,
                y: iframeRect.y,
                width: iframeRect.width,
                height: iframeRect.height,
              },
              overlay: next.coords,
            });
            setState(next);
            return;
          }
          case "mouseout": {
            smartIframeDebug("hover", "mouseout → hide overlay", {
              selector: e.data.selector,
            });
            return setState(initialState);
          }
          case "productschema":
            smartIframeDebug("message←", "productschema", e.data);
            setProductSchemaFound(true);
            return;
          case "selectorRecomputed": {
            smartIframeDebug("message←", "selectorRecomputed", {
              selector: e.data.selector,
              text: e.data.text,
              mode: e.data.mode,
            });
            if (onClick) {
              const sel = e.data.selector;
              const text = e.data.text;
              onClick({ selector: sel, dimensions: null as any, text });
            }
            return;
          }

          default: {
            return;
          }
        }
      }
    },
    [onClick, targets, sendMessage, setProductSchemaFound, notifyIframeReady]
  );

  useEvent("message", logElement);

  return (
    <Box position="relative" height="100%">
      {!loaded && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          className={classes.loading}
        >
          <CircularProgress size={"2rem"} />
        </Box>
      )}

      <IframeResizer
        forwardRef={iframeRef}
        onMouseLeave={resetRect}
        onLoad={() => {
          setLoaded(true);
          smartIframeDebug("lifecycle", "iframe onLoad");
        }}
        style={{
          height: "100%",
          width,
          border: 0,
        }}
        id="smartiframe"
        title="smartiframe"
        scrolling={true}
        src={`${appendHashParams(removeTrailingSlash(src), [
          debugParam,
          "true",
        ])}`}
      />

      <div id="iframeOverlay" style={getStyles()} />
    </Box>
  );
};
